import { postResourcesCreation } from '@/api/functions'
import { Codename } from '@/api/types/workstation'
import { getCurrentUserIdToken } from '@/services/auth'
import { WorkstationData, WorkstationUsageTime } from '@/store/types/workstation'
import { errorIfInvalidResponse } from './errors'
import {
  updateRootVolumeWorkstationURL,
  composedFetch,
  deleteWorkstationURL,
  getShareWorkstationsURL,
  getTeradiciConnectEndpoint,
  getUserRemainingHoursAPIEndpoint,
  getWorkstationURL,
} from './helpers'

/**
 * Gets a list of uuids of workstations shared with a given user, handles errors if necessary.
 * @returns the uuid list
 */
export async function apiGetShareeWorkstationsUUIDs(): Promise<string[]> {
  const response = await composedFetch(
    'GET',
    getShareWorkstationsURL(),
    await getCurrentUserIdToken(),
  )
  await errorIfInvalidResponse(response)
  return await response.json()
}
/**
 * Converts argument object into shape required by the API, calls helper for a hit, handles errors or returns value.
 * @param name workstation name
 * @param codename workstation codename
 * @returns (not sure if necessary) the connector success response.
 */
export const apiCreateSingleWorkstation = async (name: string, codename: string): Promise<void> => {
  if (!name) {
    throw new Error('Provide name to create a workstation.')
  }

  const setupInfoObject = {
    workstations: [
      {
        name,
        codename,
      },
    ],
    drives: [],
  }
  // actual endpoint hit
  const response = await postResourcesCreation(setupInfoObject)
  await errorIfInvalidResponse(response)
  return await response.json()
}

export const deleteWorkstation = async (workstationUuid: string): Promise<any> => {
  const response = await composedFetch(
    'DELETE',
    deleteWorkstationURL(workstationUuid),
    await getCurrentUserIdToken(),
  )

  await errorIfInvalidResponse(response, 'Failed to delete workstation')
  return 'success'
}

async function workstationPatch(uuid: string, bodyObj?: WorkstationData): Promise<Response> {
  return composedFetch('PATCH', getWorkstationURL(uuid), await getCurrentUserIdToken(), bodyObj)
}

export const apiChangeWorkstationCodename = async (
  uuid: string,
  codename: Codename,
): Promise<void> => {
  const setupInfoObject: WorkstationData = {
    codename,
  }

  const response = await workstationPatch(uuid, setupInfoObject)
  await errorIfInvalidResponse(response)
}

export const apiChangeWorkstationName = async (uuid: string, name: Codename): Promise<void> => {
  const setupInfoObject: WorkstationData = {
    name,
  }
  const response = await workstationPatch(uuid, setupInfoObject)
  await errorIfInvalidResponse(response)
}

export async function getUsageTimeData() {
  const response = await composedFetch(
    'GET',
    getUserRemainingHoursAPIEndpoint(),
    await getCurrentUserIdToken(),
  )
  await errorIfInvalidResponse(response, 'Failed to load usage information.')

  // todo => translate the snake_cased response to WorkstationUsageTime
  const json = await response.json()
  const { total_hours_available, hours_remaining, cycle_reset_ms_timestamp } = json
  const result: WorkstationUsageTime = {
    totalHours: total_hours_available,
    hoursRemaining: hours_remaining,
    cycleResetTimestamp: cycle_reset_ms_timestamp,
  }

  return result
}

// root_storage endpoint expects
type RootVolumeIn = {
  root_volume_throughput?: number
  root_volume_increment_size_gb?: number
}

export async function apiModifyRootVolume(workstationUuid: string, root_volume_increment_size_gb?: number, root_volume_throughput?: number) {
  const rootVolumeIn: RootVolumeIn = {
    root_volume_throughput,
    root_volume_increment_size_gb,
  }
  const response = await composedFetch(
    'PATCH',
    updateRootVolumeWorkstationURL(workstationUuid),
    await getCurrentUserIdToken(),
    rootVolumeIn,
  )
  await errorIfInvalidResponse(response)
  return await response.json()
}

export async function getTeradiciConnection(workstationUuid: string, signal: AbortSignal) {
  const response = await composedFetch(
    'GET',
    getTeradiciConnectEndpoint(workstationUuid),
    await getCurrentUserIdToken(),
    null,
    { signal },
  )
  await errorIfInvalidResponse(response, 'Failed to load usage information.')
  return response
}
